import type { Rng } from '../rng/seededRandom';
import {
  PROJECTILE_RADIUS,
  PROJECTILE_SPEED,
  PROJECTILE_STEER_RATE,
  distance,
  normalizeAngle,
} from './combat';
import type { Projectile, Vec3 } from './types';

/** Uses the same horizontal stick input as a human; never homes on hidden enemy state. */
export function steerProjectile(
  shot: Projectile,
  target: Vec3,
  velocity: Vec3,
  lead: number,
  slip: number,
  response: number,
): number {
  if (!shot.steerable) return 0;
  const dx = target.x - shot.pos.x,
    dz = target.z - shot.pos.z;
  // A shot that has passed the target must not try to loop back.
  if (dx * shot.vel.x + dz * shot.vel.z <= 0) return 0;
  const aim = leadPoint(shot.pos, target, velocity, lead);
  const error = normalizeAngle(
    Math.atan2(aim.x - shot.pos.x, aim.z - shot.pos.z) + slip - Math.atan2(shot.vel.x, shot.vel.z),
  );
  return Math.max(-1, Math.min(1, error / (PROJECTILE_STEER_RATE * Math.max(response, 1 / 30))));
}

/**
 * Where to aim so a projectile meets a target moving at a steady velocity; `lead` 0…1 blends
 * from no lead to the full amount. Vertical motion is ignored: hit boxes are tall and a jump
 * rarely takes the centre out of one.
 */
export function leadPoint(from: Vec3, target: Vec3, vel: Vec3, lead: number): Vec3 {
  if (lead <= 0) return target;
  let t = distance(from, target) / PROJECTILE_SPEED;
  for (let i = 0; i < 2; i++) {
    const p = { x: target.x + vel.x * t, y: target.y, z: target.z + vel.z * t };
    t = distance(from, p) / PROJECTILE_SPEED;
  }
  const k = t * lead;
  return { x: target.x + vel.x * k, y: target.y, z: target.z + vel.z * k };
}

/** Yaw and pitch from an eye to a point. */
export function anglesTo(eye: Vec3, at: Vec3): { yaw: number; pitch: number } {
  const dx = at.x - eye.x;
  const dz = at.z - eye.z;
  return {
    yaw: Math.atan2(dx, dz),
    pitch: Math.atan2(at.y - eye.y, Math.max(0.01, Math.hypot(dx, dz))),
  };
}

/** Turns an angle towards a wanted one, no faster than `rate` radians per second. */
export function turnToward(current: number, wanted: number, rate: number, dt: number): number {
  const diff = normalizeAngle(wanted - current);
  const step = rate * dt;
  if (Math.abs(diff) <= step) return normalizeAngle(wanted);
  return normalizeAngle(current + Math.sign(diff) * step);
}

/**
 * A Gaussian random slip centred on true aim and clamped to [−spread, spread]. The requested
 * spread is two standard deviations, so most shots sit near the target while the named cone is a
 * firm bound. Box-Muller is fed only by the battle AI's seeded generator.
 */
export function aimSlip(rng: Rng, spread: number): number {
  if (spread <= 0) return 0;
  const u1 = Math.max(Number.EPSILON, rng.next());
  const u2 = rng.next();
  const normal = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.max(-spread, Math.min(spread, normal * (spread / 2)));
}

/** Angular half-size, seen from `dist` metres, of a hit box `halfExtent` across grown by the projectile radius. */
export function hitAngle(halfExtent: number, dist: number): number {
  return Math.atan2(halfExtent + PROJECTILE_RADIUS, Math.max(0.5, dist));
}
